import UsersService from './UsersService'

interface UsersResponse {
  users: any[]
  count: number
}

/**
 * Fetches all users for a location using paginated search endpoint
 * Replaces the deprecated getByLocationId endpoint
 *
 * Uses parallel fetching with concurrency limit for optimal performance
 *
 * @param locationId - Location ID to fetch users for
 * @param companyId - Company ID (optional, improves query performance)
 * @param batchSize - Number of users to fetch per request (default: 500)
 * @param maxConcurrency - Maximum number of parallel requests (default: 3)
 * @returns Promise with all users and total count
 */
export async function getAllUsersByLocationId(
  locationId: string,
  companyId?: string,
  batchSize: number = 500,
  maxConcurrency: number = 3
): Promise<UsersResponse> {
  if (!locationId) {
    throw new Error('LocationId is required')
  }

  // First, get the total count to determine how many batches we need
  let totalCount = 0
  let firstBatchUsers: any[] = []

  try {
    // Fetch first batch to get total count
    const firstResponse = await UsersService.search(
      companyId || '',
      0,
      batchSize,
      undefined, // query
      undefined, // role
      undefined, // type
      locationId
    )

    if (!firstResponse || !firstResponse.users) {
      return { users: [], count: 0 }
    }

    firstBatchUsers = firstResponse.users || []
    totalCount = firstResponse.count || firstBatchUsers.length

    // If we got all users in first batch, return early
    if (firstBatchUsers.length < batchSize || firstBatchUsers.length >= totalCount) {
      return {
        users: firstBatchUsers,
        count: totalCount,
      }
    }

    // Calculate number of remaining batches
    const totalBatches = Math.ceil(totalCount / batchSize)
    const remainingBatches = totalBatches - 1

    if (remainingBatches === 0) {
      return {
        users: firstBatchUsers,
        count: totalCount,
      }
    }

    // Create batch tasks for remaining batches
    const batchTasks: Array<{ skip: number; batchIndex: number }> = []
    for (let i = 1; i < totalBatches; i++) {
      batchTasks.push({
        skip: i * batchSize,
        batchIndex: i,
      })
    }

    // Fetch remaining batches in parallel with concurrency limit
    const remainingUsers = await fetchBatchesInParallel(
      batchTasks,
      companyId || '',
      batchSize,
      locationId,
      maxConcurrency
    )

    // Combine all users
    const allUsers = [...firstBatchUsers, ...remainingUsers]

    return {
      users: allUsers,
      count: totalCount,
    }
  } catch (error) {
    console.error('Error fetching users by location:', error)
    // Return what we have so far, or throw if first batch failed
    if (firstBatchUsers.length > 0) {
      return {
        users: firstBatchUsers,
        count: totalCount || firstBatchUsers.length,
      }
    }
    throw error
  }
}

/**
 * Fetches multiple batches in parallel with concurrency limit
 *
 * @param batchTasks - Array of batch tasks to fetch
 * @param companyId - Company ID
 * @param batchSize - Batch size
 * @param locationId - Location ID
 * @param maxConcurrency - Maximum concurrent requests
 * @returns Array of all users from all batches
 */
async function fetchBatchesInParallel(
  batchTasks: Array<{ skip: number; batchIndex: number }>,
  companyId: string,
  batchSize: number,
  locationId: string,
  maxConcurrency: number
): Promise<any[]> {
  const allUsers: any[] = []
  const errors: Error[] = []

  // Process batches in chunks based on concurrency limit
  for (let i = 0; i < batchTasks.length; i += maxConcurrency) {
    const batchChunk = batchTasks.slice(i, i + maxConcurrency)

    const chunkPromises = batchChunk.map(async ({ skip, batchIndex }) => {
      try {
        const response = await UsersService.search(
          companyId,
          skip,
          batchSize,
          undefined, // query
          undefined, // role
          undefined, // type
          locationId
        )

        if (response && response.users) {
          return { users: response.users, batchIndex }
        }
        return { users: [], batchIndex }
      } catch (error) {
        console.error(`Error fetching batch ${batchIndex} (skip=${skip}):`, error)
        errors.push(error as Error)
        return { users: [], batchIndex }
      }
    })

    const chunkResults = await Promise.all(chunkPromises)

    // Sort by batchIndex to maintain order
    chunkResults.sort((a, b) => a.batchIndex - b.batchIndex)

    // Add users in order
    chunkResults.forEach(result => {
      allUsers.push(...result.users)
    })

    // If we have errors and this is the first chunk, throw to fail fast
    if (errors.length > 0 && i === 0) {
      throw errors[0]
    }
  }

  // Log warnings for errors in later batches
  if (errors.length > 0) {
    console.warn(`Encountered ${errors.length} errors while fetching batches, but continuing with available data`)
  }

  return allUsers
}
